import { useQuery } from '@apollo/client/react';
import { MY_PERMISSIONS_QUERY, MyPermissionsResponse } from '@/graphql/queries/permissions';
import { apolloClientV2 } from '@/lib/apollo-client-v2';

/**
 * Hook to get and check user permissions
 * 
 * @returns Object with permissions array and helper functions
 */
export const usePermissions = () => {
  const { data, loading, error } = useQuery<MyPermissionsResponse>(MY_PERMISSIONS_QUERY, {
    fetchPolicy: 'cache-and-network',
    client: apolloClientV2,
  });

  const permissions = data?.myPermissions || [];

  /**
   * Check if user has a specific permission
   */
  const hasPermission = (permission: string): boolean => {
    return permissions.includes(permission);
  };

  /**
   * Check if user has any of the specified permissions
   */
  const hasAnyPermission = (permissionList: string[]): boolean => {
    return permissionList.some(perm => permissions.includes(perm));
  };

  /**
   * Check if user has all of the specified permissions
   */
  const hasAllPermissions = (permissionList: string[]): boolean => {
    return permissionList.every(perm => permissions.includes(perm));
  };

  return {
    permissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    loading,
    error,
  };
};
